import { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { FormGroup } from "reactstrap";
import { Box, Button, ButtonColors, Typography } from "@talentech/components";
import { Question } from "@/types/feedback.types";
import { getQuestionText } from "@/utils/formUtils";
import { useTranslation } from "react-i18next";
import useFormLanguage from "@/utils/useFormLanguage.hook";

const NPSRating = (props: Question) => {
  const { t } = useTranslation();
  const { id, scale, required } = props;
  const formLanguageCode = useFormLanguage();
  const label = getQuestionText(props, formLanguageCode);

  const { control } = useFormContext();

  const fieldName = id.toString();

  const maxNPS = (scale?.max ?? 0) - (scale?.min ?? 0) + 1;

  const nps = useMemo(
    () => Array.from({ length: maxNPS }, (_, index) => index),
    [],
  );

  const isNPSFilled = (npsIndex: number, value?: number) => {
    if (!value) return false;
    return npsIndex === Number(value);
  };

  const onNPSClick = (npsIndex: number, onChange: (value: string) => void) => {
    onChange(String(npsIndex));
  };

  return (
    <FormGroup
      data-cy="question-nps-rating"
      tag="fieldset"
      noMargin
      className="w-100 d-flex flex-column gap-0 p-4 rounded-3 bg-white"
    >
      <span className="fs-6 fw-semibold text-break mb-3 text-300">
        {label}
        {required && <span className="text-danger d-none">&nbsp;*</span>}
      </span>

      <Box flex className="flex-column gap-2">
        <Controller
          {...{ control }}
          name={fieldName}
          render={({ field }) => (
            <Box
              flex
              rounded
              className="flex-column align-items-start align-self-stretch bg-gray-100 gap-2 p-3 p-sm-4 pb-sm-3"
            >
              <Box
                flex
                className="justify-content-center justify-content-md-between align-items-start align-self-stretch flex-wrap"
              >
                {nps.map((_, index) => (
                  <Button
                    key={index}
                    onClick={() => onNPSClick(index, field.onChange)}
                    color={ButtonColors.PRIMARY}
                    className={`btn btn-sm rounded-circle align-items-center justify-content-center d-flex m-1 ${
                      isNPSFilled(index, field.value)
                        ? "btn-primary"
                        : "btn-outline-secondary"
                    }`}
                    style={{
                      width: "40px",
                      height: "40px",
                    }}
                    label={index + (scale?.min ?? 0)}
                    aria-pressed={isNPSFilled(index, field.value)}
                    aria-label={t("Form.NPSRatingField.AriaLabel", {
                      value: index + (scale?.min ?? 0),
                    })}
                  />
                ))}
              </Box>

              <Box
                flex
                className="justify-content-between align-items-start align-self-stretch"
              >
                <Typography className="fs-12 m-0 text-300">
                  {t("Form.NPSRatingField.LeftText")}
                </Typography>

                <Typography className="fs-12 m-0 text-300">
                  {t("Form.NPSRatingField.RightText")}
                </Typography>
              </Box>
            </Box>
          )}
        />
      </Box>
    </FormGroup>
  );
};

export default NPSRating;
